import { useEffect, useState } from 'react'
import { useOnboardingStore, STEPS } from '../stores/onboardingStore'
import { useStepSubmit } from '../context/StepSubmitContext'
import { apiPost } from '../lib/api'
import SummaryCard from '../components/form/SummaryCard'
import StepLayout from '../components/form/StepLayout'
import Band from '../components/form/Band'

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) return resolve(null)
    if (typeof file === 'string') return resolve(file)
    if (typeof File !== 'undefined' && !(file instanceof File))
      return resolve(null)
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

function Row({ label, value }) {
  return (
    <div className="summary-row">
      <span className="summary-row-key">{label}</span>
      <span className="summary-row-value">
        {value !== undefined && value !== null && value !== ''
          ? value
          : <em>Not set</em>}
      </span>
    </div>
  )
}

function locLabel(l, i) {
  return (l?.addressLine1 || '').trim() || `Location ${i + 1}`
}

function fmtPrice(n) {
  if (n === undefined || n === null || n === '') return null
  const num = Number(n)
  if (Number.isNaN(num)) return null
  return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

export default function StepReview() {
  const step = STEPS.find((s) => s.id === 'review')
  const formData = useOnboardingStore((s) => s.formData)
  const goToStepId = useOnboardingStore((s) => s.goToStepId)
  const markComplete = useOnboardingStore((s) => s.markComplete)
  const { setHandler } = useStepSubmit()

  const [liteSubmitting, setLiteSubmitting] = useState(false)
  const [liteError, setLiteError] = useState(null)
  const [liteIssues, setLiteIssues] = useState(null)

  useEffect(() => {
    // Resolve the logo to a submittable form (data URL for uploads, remote URL
    // for brandfetch/favicon). Shared by both submit paths below.
    const resolveLogo = async (fd) => {
      const logo = fd?.details?.logo
      let logoFile = null
      let logoRemoteUrl = null
      if (logo?.source === 'upload' && logo.file) {
        logoFile = await fileToDataUrl(logo.file).catch(() => null)
      } else if (
        (logo?.source === 'brandfetch' || logo?.source === 'favicon') &&
        logo.url
      ) {
        logoRemoteUrl = logo.url
      }
      return { logoFile, logoRemoteUrl }
    }

    const submit = async () => {
      if (liteSubmitting) return
      setLiteError(null)
      setLiteIssues(null)
      setLiteSubmitting(true)
      try {
        const fd = useOnboardingStore.getState().formData || {}
        const { suggested: _suggested, ...rest } = fd
        const { logoFile, logoRemoteUrl } = await resolveLogo(fd)

        // Normal mode — Agreements (Step 11) is hidden from the flow, so Review
        // is terminal. Submit to /submit with the same shape Agreements sends,
        // but with all signatures/contract PDFs null (no signing step).
        const completedAt = new Date().toISOString()
        const payload = {
          ...rest,
          agreements: {
            agreementsAcknowledged: true,
            signature: null,
            pepSignature: null,
            pepAnnex1Signature: null,
            pepAnnex2Signature: null,
            pepExhibitFSignature: null,
            completedAt,
          },
          providerPdf: null,
          pepPdf: null,
          logoFile,
          logoRemoteUrl,
        }

        // Mirror Step11's Network+ provider cleanup — drop fully-empty rows.
        const isNetworkPlus =
          fd?.networkplus?.isMemberPlusMember === true
        if (isNetworkPlus && Array.isArray(payload?.providers?.providers)) {
          const cleaned = payload.providers.providers.filter((p) => {
            const hasName = String(p?.fullName || '').trim() !== ''
            const hasEmail = String(p?.email || '').trim() !== ''
            const hasLocs =
              Array.isArray(p?.locationIds) && p.locationIds.length > 0
            return hasName || hasEmail || hasLocs
          })
          payload.providers = { ...payload.providers, providers: cleaned }
        }

        // Normal-mode final submit now lands in the briteOS backend so the
        // practice flips to 'submitted'. Drop the base64 logo blob (logoFile);
        // the uploaded logoRemoteUrl is what we persist.
        const slug = new URLSearchParams(window.location.search).get('p')
        if (slug) {
          const { logoFile: _logoFile, ...rest2 } = payload
          const res = await fetch(
            `${import.meta.env.VITE_ONBOARDING_OS_API_URL}/submit`,
            {
              method: 'POST',
              credentials: 'omit',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ slug, payload: rest2 }),
            },
          )
          const data = await res.json().catch(() => null)
          if (!res.ok) {
            setLiteError(
              data?.error ||
                "Couldn't submit right now — please try again or contact support@brite.live.",
            )
            return
          }
          markComplete(null)
        } else {
          // Defensive: a non-invited visitor with no ?p= slug — keep the old backend.
          const res = await apiPost('/submit', payload)
          if (!res.ok) {
            if (res.status === 400 && Array.isArray(res.data?.issues)) {
              setLiteIssues(res.data.issues)
            }
            setLiteError(
              res.data?.error ||
                "Couldn't submit right now — please try again or contact support@brite.live.",
            )
            return
          }
          markComplete(res.data?.submissionId || null, {
            awaitingCap: res.data?.awaitingCap === true,
            capName: res.data?.capName,
            capEmail: res.data?.capEmail,
          })
        }
      } catch (err) {
        setLiteError(`Submit failed: ${err?.message || 'unknown error'}`)
      } finally {
        setLiteSubmitting(false)
      }
    }
    setHandler(submit)
    return () => setHandler(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHandler, markComplete])

  const details = formData.details || {}
  const managedBillingOptedIn =
    formData.managedBilling?.acknowledged === true
  const packages = formData.managedBilling?.packages || {}
  const owners = formData.owners?.owners || []
  const locations = formData.locations?.locations || []
  const networkplus = formData.networkplus || {}
  const providers = formData.providers?.providers || []
  const kits = formData.kits?.kits || []
  const teams = formData.teams?.teams || []

  const primaryIdx = locations.findIndex((l) => l?.isPrimary === true)
  const primaryResolvedIdx = primaryIdx >= 0 ? primaryIdx : 0

  let networkPlusStatus
  if (networkplus.isMemberPlusMember === true) networkPlusStatus = 'Enabled'
  else if (networkplus.isMemberPlusMember === false)
    networkPlusStatus = 'Declined'
  else networkPlusStatus = null

  return (
    <div className="step-body">
      <StepLayout question={step.title} helper={step.subtitle}>
      <Band label="Review" description="Confirm everything looks right">

      {liteError && (
        <div
          className="field-error"
          role="alert"
          style={{ margin: 'var(--space-lg) 0', padding: 'var(--space-md)', background: 'rgba(220, 38, 38, 0.08)', borderRadius: 8 }}
        >
          <div style={{ fontWeight: 600 }}>{liteError}</div>
          {Array.isArray(liteIssues) && liteIssues.length > 0 && (
            <ul style={{ marginTop: 'var(--space-sm)', paddingLeft: 18 }}>
              {liteIssues.map((iss, i) => (
                <li key={i}>
                  {Array.isArray(iss?.path) ? iss.path.join('.') + ': ' : ''}
                  {iss?.message || JSON.stringify(iss)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {liteSubmitting && (
        <p className="step-subtitle" style={{ marginTop: 0 }}>
          Submitting…
        </p>
      )}

      <SummaryCard
        title="Practice details"
        onEdit={() => goToStepId('details')}
      >
        <Row label="Practice name" value={details.practiceName} />
        <Row
          label="Logo"
          value={details.logo?.name || (details.logo ? 'Uploaded' : null)}
        />
      </SummaryCard>

      <SummaryCard
        title="Managed Billing"
        onEdit={() => goToStepId('managedBilling')}
      >
        <Row
          label="Status"
          value={managedBillingOptedIn ? 'Opted in' : 'Declined'}
        />
      </SummaryCard>

      {managedBillingOptedIn && (
        <SummaryCard
          title="Packages"
          onEdit={() => goToStepId('managedBilling')}
        >
          {['female', 'male'].map((key) => {
            const pkg = packages[key] || {}
            const label = key === 'female' ? 'Female' : 'Male'
            const monthly = fmtPrice(pkg.monthlyPrice)
            const annual = fmtPrice(pkg.annualPrice)
            return (
              <div
                key={key}
                style={{ marginTop: key === 'male' ? 'var(--space-md)' : 0 }}
              >
                <div
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--text-3)',
                    marginBottom: 'var(--space-xs)',
                  }}
                >
                  {label}
                </div>
                <Row label="Internal name" value={pkg.internalName} />
                <Row label="Display name" value={pkg.displayName} />
                <Row label="Monthly" value={monthly ? `${monthly}/mo` : null} />
                <Row label="Annual" value={annual ? `${annual}/yr` : null} />
              </div>
            )
          })}
        </SummaryCard>
      )}

      <SummaryCard title="Owners" onEdit={() => goToStepId('owners')}>
        <Row
          label="Count"
          value={owners.length > 0 ? `${owners.length} owner(s)` : null}
        />
        {owners.length > 0 && (
          <div className="summary-list">
            {owners.map((o, i) => (
              <div className="summary-list-item" key={i}>
                <span className="summary-list-item-index">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="summary-list-item-main">
                  <span className="summary-list-item-primary">
                    {o?.fullName || <em>Unnamed owner</em>}
                  </span>
                  {o?.email && (
                    <span className="summary-list-item-secondary">
                      {o.email}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SummaryCard>

      <SummaryCard title="Locations" onEdit={() => goToStepId('locations')}>
        <Row
          label="Count"
          value={
            locations.length > 0 ? `${locations.length} location(s)` : null
          }
        />
        {locations.length > 0 && (
          <div className="summary-list">
            {locations.map((l, i) => {
              const isPrimary = i === primaryResolvedIdx
              return (
                <div className="summary-list-item" key={i}>
                  <span className="summary-list-item-index">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="summary-list-item-main">
                    <span className="summary-list-item-primary">
                      {locLabel(l, i)}
                      {isPrimary && (
                        <span
                          className="primary-tag"
                          style={{ marginLeft: 8 }}
                        >
                          Primary
                        </span>
                      )}
                    </span>
                    <span className="summary-list-item-secondary">
                      {[l?.city, l?.state, l?.zip].filter(Boolean).join(', ') ||
                        '—'}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </SummaryCard>

      <SummaryCard title="Network+" onEdit={() => goToStepId('networkplus')}>
        <Row label="Status" value={networkPlusStatus} />
        {networkplus.isMemberPlusMember === true && (() => {
          const promo = formData?.stripe?.networkPlus?.appliedPromotionCode
          const finalCents = typeof promo?.finalAmount === 'number' ? promo.finalAmount : 499700
          const baseLabel = `$${(finalCents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
          const couponNote = promo?.code
            ? ` · ${promo.code}${promo.label ? ' (' + promo.label + ')' : ''}`
            : ''
          const isPaid = networkplus.paymentStatus === 'paid'
          const value = isPaid
            ? `${baseLabel}${couponNote} · ✓ Paid`
            : finalCents <= 0
              ? `Free${couponNote}`
              : `${baseLabel}${couponNote} · pending payment on the next step`
          return <Row label="Setup fee" value={value} />
        })()}
      </SummaryCard>

      <SummaryCard title="Providers" onEdit={() => goToStepId('providers')}>
        <Row
          label="Count"
          value={
            providers.length > 0 ? `${providers.length} provider(s)` : null
          }
        />
        {providers.length > 0 && (
          <div className="summary-list">
            {providers.map((p, i) => {
              const labels = (p?.locationIds || [])
                .map((id) => {
                  const ix = locations.findIndex((l) => l?.id === id)
                  return ix >= 0 ? locLabel(locations[ix], ix) : null
                })
                .filter(Boolean)
              return (
                <div className="summary-list-item" key={i}>
                  <span className="summary-list-item-index">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="summary-list-item-main">
                    <span className="summary-list-item-primary">
                      {p?.fullName || <em>Unnamed provider</em>}
                    </span>
                    <span className="summary-list-item-secondary">
                      {labels.length > 0
                        ? `Covers: ${labels.join(', ')}`
                        : 'No locations assigned'}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </SummaryCard>

      <SummaryCard title="Kits" onEdit={() => goToStepId('kits')}>
        <Row
          label="Count"
          value={kits.length > 0 ? `${kits.length} kit(s)` : null}
        />
        {kits.length > 0 && (
          <div className="summary-list">
            {kits.map((k, i) => {
              const locationLabels = (k?.locationIds || [])
                .map((id) => {
                  const ix = locations.findIndex((l) => l?.id === id)
                  return ix >= 0 ? locLabel(locations[ix], ix) : null
                })
                .filter(Boolean)
              const custom = k?.customShipTo
              const customLabel =
                custom && (custom.addressLine1 || custom.city)
                  ? `Custom: ${[custom.addressLine1, custom.city]
                      .filter(Boolean)
                      .join(', ')}`
                  : null
              const shipsTo =
                customLabel ||
                (locationLabels.length > 0
                  ? locationLabels.join(', ')
                  : null)
              return (
                <div className="summary-list-item" key={i}>
                  <span className="summary-list-item-index">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="summary-list-item-main">
                    <span className="summary-list-item-primary">
                      Qty {k?.quantity ?? 1} — {k?.recipientName || <em>Unnamed</em>}
                    </span>
                    <span className="summary-list-item-secondary">
                      Ships to: {shipsTo || <em>Not set</em>}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </SummaryCard>

      <SummaryCard title="Team members" onEdit={() => goToStepId('teams')}>
        <Row
          label="Count"
          value={
            teams.length > 0
              ? `${teams.length} member(s)`
              : 'No team members — skipped'
          }
        />
        {teams.length > 0 && (
          <div className="summary-list">
            {teams.map((t, i) => (
              <div className="summary-list-item" key={i}>
                <span className="summary-list-item-index">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="summary-list-item-main">
                  <span className="summary-list-item-primary">
                    {t?.fullName || <em>Unnamed</em>}
                  </span>
                  <span className="summary-list-item-secondary">
                    {t?.email || '—'}
                    {t?.role ? ` · ${t.role}` : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </SummaryCard>
      </Band>
      </StepLayout>
    </div>
  )
}
